import React, { useCallback, useEffect, useRef, useState } from "react";
import { Button } from "react-bootstrap";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";

import type { AppDispatch } from "@/app/store";
import {
  BulkProgressModal,
  type BulkRunProgress,
} from "@/features/processing/lifecycleModals";
import {
  clearActiveBulkOp,
  expandActiveBulkOp,
  minimizeActiveBulkOp,
  readActiveBulkOp,
  subscribeActiveBulkOp,
  type ActiveBulkOpSession,
} from "@/features/processing/activeBulkOpSession";
import { processingJobsApi } from "@/features/processing/processingApi";
import type { ProcessingBulkOpStatus } from "@/features/processing/processingTypes";

const TERMINAL = new Set([
  "completed",
  "completed_with_exceptions",
  "cancelled",
  "failed",
  "auto_stopped",
]);

type Waiter = {
  resolve: (status: ProcessingBulkOpStatus) => void;
  reject: (err: Error) => void;
};

const waitersById = new Map<string, Waiter[]>();

function addWaiter(bulkOpId: string, waiter: Waiter): () => void {
  const list = waitersById.get(bulkOpId) || [];
  list.push(waiter);
  waitersById.set(bulkOpId, list);
  return () => {
    const next = (waitersById.get(bulkOpId) || []).filter((w) => w !== waiter);
    if (next.length === 0) waitersById.delete(bulkOpId);
    else waitersById.set(bulkOpId, next);
  };
}

function settleWaiters(bulkOpId: string, status: ProcessingBulkOpStatus): void {
  const list = waitersById.get(bulkOpId) || [];
  waitersById.delete(bulkOpId);
  for (const w of list) w.resolve(status);
}

/**
 * Resolves when the shell host observes a terminal BulkOp status.
 * Used by Song Processing page flows that still need to finalize UI state.
 */
export function waitForActiveBulkOpTerminal(
  bulkOpId: string,
): Promise<ProcessingBulkOpStatus> {
  return new Promise((resolve, reject) => {
    addWaiter(bulkOpId, { resolve, reject });
  });
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => {
    window.setTimeout(resolve, ms);
  });
}

/**
 * App-shell host: keeps durable BulkOp progress visible across routes.
 * Minimized → compact chip (browse freely). Expanded → progress modal.
 * Navigating back onto Song Processing from another route re-expands.
 */
export function ActiveBulkOpProgressHost(): React.ReactElement | null {
  const dispatch = useDispatch<AppDispatch>();
  const location = useLocation();
  const navigate = useNavigate();
  const [session, setSession] = useState<ActiveBulkOpSession | null>(() =>
    readActiveBulkOp(),
  );
  const [progress, setProgress] = useState<BulkRunProgress | null>(null);
  const prevPathRef = useRef(location.pathname);

  useEffect(() => subscribeActiveBulkOp(() => setSession(readActiveBulkOp())), []);

  // Only auto-expand when entering Song Processing from another route.
  // Minimizing while already here must stick (chip), or Hide is a no-op.
  useEffect(() => {
    const prev = prevPathRef.current;
    prevPathRef.current = location.pathname;
    if (!session?.minimized) return;
    const onProcessing = location.pathname.startsWith("/processing-jobs");
    const wasOnProcessing = prev.startsWith("/processing-jobs");
    if (onProcessing && !wasOnProcessing) {
      expandActiveBulkOp();
    }
  }, [location.pathname, session?.minimized]);

  useEffect(() => {
    if (!session?.bulkOpId) {
      setProgress(null);
      return;
    }
    let cancelled = false;
    const bulkOpId = session.bulkOpId;
    const verb = session.verb;

    const poll = async () => {
      for (let attempt = 0; attempt < 3600; attempt += 1) {
        if (cancelled) return;
        const sub = dispatch(
          processingJobsApi.endpoints.getProcessingBulkOp.initiate(
            { bulkOpId },
            { forceRefetch: true },
          ),
        );
        let status: ProcessingBulkOpStatus;
        try {
          status = await sub.unwrap();
        } catch {
          sub.unsubscribe();
          if (cancelled) return;
          await sleep(2000);
          continue;
        }
        sub.unsubscribe();
        if (cancelled) return;

        const c = status.counters || {
          examined: 0,
          succeeded: 0,
          skipped: 0,
          conflicts: 0,
          failed: 0,
        };
        const preparing =
          status.status === "preparing" || status.status === "queued";
        setProgress({
          verb,
          phase: preparing ? "collecting" : "running",
          totalJobs: status.totalMatched || 0,
          batch: 1,
          totalBatches: 1,
          completed: c.examined,
          succeeded: c.succeeded,
          skipped: c.skipped + c.conflicts,
          failed: c.failed,
          durable: true,
        });

        if (TERMINAL.has(status.status)) {
          settleWaiters(bulkOpId, status);
          clearActiveBulkOp();
          setProgress(null);
          dispatch(
            processingJobsApi.util.invalidateTags([
              { type: "ProcessingJobs", id: "LIST" },
            ]),
          );
          return;
        }
        await sleep(1000);
      }
    };

    void poll();
    return () => {
      cancelled = true;
    };
  }, [dispatch, session?.bulkOpId, session?.verb]);

  const onHide = useCallback(() => {
    minimizeActiveBulkOp();
    toast(
      "Progress hidden — use the chip (bottom-right) or open another page. Click the chip anytime to watch again.",
      { duration: 7000 },
    );
  }, []);

  if (!session) return null;

  if (session.minimized) {
    const label =
      progress == null
        ? `${session.verb}…`
        : progress.phase === "collecting"
          ? `${session.verb} (preparing)…`
          : `${session.verb} ${progress.completed.toLocaleString()} / ${Math.max(progress.totalJobs, 1).toLocaleString()}`;
    return (
      <div
        style={{
          position: "fixed",
          right: 20,
          bottom: 20,
          zIndex: 1080,
          display: "flex",
          gap: 8,
          alignItems: "center",
        }}
      >
        <Button
          variant="dark"
          onClick={() => {
            expandActiveBulkOp();
            if (!location.pathname.startsWith("/processing-jobs")) {
              navigate("/processing-jobs");
            }
          }}
          style={{
            color: "#fff",
            boxShadow: "0 4px 16px rgba(0,0,0,0.25)",
            borderRadius: 8,
            padding: "10px 14px",
            fontWeight: 600,
          }}
        >
          {label}
        </Button>
      </div>
    );
  }

  return (
    <BulkProgressModal progress={progress} onContinueInBackground={onHide} />
  );
}
